import axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig, AxiosError } from 'axios';

import { crash } from '@flyblack/common/services/crash';

export interface HttpResponse<T> extends AxiosResponse<T> {}
export interface HttpRequestConfig extends AxiosRequestConfig {
  sentry?: { disabled?: boolean; status?: number[] };
}
export interface HttpError<T> extends AxiosError<T> {
  config: HttpRequestConfig;
}

export class HttpClient {
  protected client: AxiosInstance;

  constructor(config?: HttpRequestConfig) {
    this.client = axios.create(config);

    this.client.interceptors.request.use(undefined, this.report);
    this.client.interceptors.response.use(undefined, this.report);
  }

  private shouldReport(sentry: HttpRequestConfig['sentry'], response?: HttpResponse<any>) {
    if (!sentry) return true;

    if (sentry.disabled || !response) return false;

    if (!sentry.status) return true;

    return !sentry.status.includes(response.status);
  }

  protected getReportOptions(error: HttpError<any>) {
    return error.isAxiosError
      ? {
          config: { headers: error.config.headers, data: error.config.data, params: error.config.params },
          response: error.response ? error.response.data : {}
        }
      : {};
  }

  private report = (error: HttpError<any>) => {
    const shouldReport =
      this.shouldReport((this.client.defaults as HttpRequestConfig).sentry, error.response) &&
      this.shouldReport(error.config.sentry, error.response);

    if (!shouldReport) throw error;

    return crash.throw(error, this.getReportOptions(error));
  };

  protected unwrap = <T>(response: HttpResponse<T>) => response.data;

  get<T>(url: string, params?: object, config?: HttpRequestConfig) {
    return this.client
      .get<T>(url, { params, ...config })
      .then(this.unwrap);
  }

  post<T>(url: string, data?: object, config?: HttpRequestConfig) {
    return this.client.post<T>(url, data, config).then(this.unwrap);
  }

  put<T>(url: string, data?: object, config?: HttpRequestConfig) {
    return this.client.put<T>(url, data, config).then(this.unwrap);
  }

  delete<T>(url: string, config?: HttpRequestConfig) {
    return this.client.delete<T>(url, config).then(this.unwrap);
  }
}
